// store/appStore.ts
import { defineStore } from "pinia";
import type { BroadcastChannelMsg } from "@/types";

export const useStore = defineStore("appStore", {
  state: () => ({
    axiosConnections: 0,
    tooManyRequestTimes: 0,
    requestFailedTimes: 0,
    smallLoadConnection: 0,
    pageName: "",
    isDarkTheme: false, // 若 localStorage 有值或时间过晚，则会在 app.vue 修改切换
    isAutoSwitchDarkTheme:
      (window.localStorage.getItem("auto-switch-dark-theme") || "false") ===
      "true",
    isShowUniverse:
      (window.localStorage.getItem("show-universe") || "false") === "true",
    isShowLive2d:
      (window.localStorage.getItem("live2d-isShowLive2d") || "false") ===
      "true",
    isMenuCollapsed:
      (window.localStorage.getItem("menu-collapsed") || "false") === "true",
    loginAuthentication: window.localStorage.getItem("loginAuthentication"),
    isMdMetaColumnFold: window.localStorage.getItem("isMdMetaColumnFold")
      ? window.localStorage.getItem("isMdMetaColumnFold") === "true"
      : false,
  }),
  actions: {
    establishConnection() {
      this.axiosConnections++;
    },
    finishConnection() {
      if (this.axiosConnections - 1 <= 0) {
        this.axiosConnections = 0;
        return;
      }
      this.axiosConnections--;
    },
    sendTooManyRequest() {
      this.tooManyRequestTimes++;
    },
    sendRequestFailed() {
      this.requestFailedTimes++;
    },
    establishSmallLoadConnection() {
      this.smallLoadConnection++;
    },
    finishSmallLoadConnection() {
      this.smallLoadConnection--;
    },
    setPageName(pageName: string) {
      this.pageName = pageName;
    },
    switchDarkTheme(isRecvBroadcast = false) {
      this.isDarkTheme = !this.isDarkTheme;
      const htmlElement = document.querySelector("html");
      if (htmlElement) {
        htmlElement.setAttribute(
          "data-theme",
          this.isDarkTheme ? "dark" : "light"
        );
      } else {
        console.error("htmlElement is null");
        return;
      }

      const theme = this.isDarkTheme ? "dark" : "light";
      document.documentElement.style.setProperty(
        "--page-bg",
        `var(--page-bg-${theme === "dark" ? "dark" : "default"})`
      );
      document.documentElement.style.setProperty(
        "--card-bg",
        `var(--card-bg-${theme === "dark" ? "dark" : "default"})`
      );
      document.documentElement.style.setProperty(
        "--font-color",
        `var(--font-color-${theme === "dark" ? "dark" : "default"})`
      );
      document.documentElement.style.setProperty(
        "--font-color-reverse",
        theme === "dark"
          ? "var(--font-color-default)"
          : "var(--font-color-dark)"
      );
      document.documentElement.style.setProperty(
        "--md-bg",
        `var(--md-bg-${theme === "dark" ? "dark" : "default"})`
      );

      localStorage.setItem("console-theme-mode", theme);
      if (!isRecvBroadcast) {
        // 同步广播至其他标签页
        window.broadcastChannel.postMessage({
          type: "theme",
          sender: window.tabId,
          msg: this.isDarkTheme,
        } as BroadcastChannelMsg);
      }
    },
    setLive2DShow(isShow: boolean) {
      this.isShowLive2d = isShow;
      window.localStorage.setItem("live2d-isShowLive2d", String(isShow));
    },
    setMenuCollapsed(isCollapsed: boolean) {
      this.isMenuCollapsed = isCollapsed;
      window.localStorage.setItem("menu-collapsed", String(isCollapsed));
    },
    toggleMenuCollapse() {
      this.isMenuCollapsed = !this.isMenuCollapsed;
      window.localStorage.setItem(
        "menu-collapsed",
        String(this.isMenuCollapsed)
      );
    },
    setLoginAuthentication(authKey: string) {
      this.loginAuthentication = authKey;
      window.localStorage.setItem("loginAuthentication", authKey);
    },
    switchAutoSwitchDarkTheme() {
      this.isAutoSwitchDarkTheme = !this.isAutoSwitchDarkTheme;
      window.localStorage.setItem(
        "auto-switch-dark-theme",
        String(this.isAutoSwitchDarkTheme)
      );
    },
    switchShowUniverse() {
      this.isShowUniverse = !this.isShowUniverse;
      window.localStorage.setItem("show-universe", String(this.isShowUniverse));
      document
        .querySelector("html")
        ?.setAttribute("data-not-show-universe", String(this.isShowUniverse));
    },
    setIsMdMetaColumnFold(isFold: boolean) {
      this.isMdMetaColumnFold = isFold;
      window.localStorage.setItem("isMdMetaColumnFold", String(isFold));
    },
  },
});
